import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  AlertCircle,
  Trophy,
  BarChart3,
  Scale,
  Percent,
  UserPlus,
  ClipboardList,
  Target,
} from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import { Button, Card, EmptyState, Meter, Skeleton, StatTile, Tabs, formatEuro } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { LeadSheet } from '@/features/leads/LeadForm';
import { HeuteFaellig } from './components/HeuteFaellig';
import { GoalTracker } from './components/GoalTracker';
import { PipelineChart } from './components/PipelineChart';
import { AnfragenList } from './components/AnfragenList';
import { TasksWidget } from './components/TasksWidget';
import { ActivityFeedWidget } from './components/ActivityFeedWidget';
import { RevenueEntriesWidget } from './components/RevenueEntriesWidget';

type DashboardTab = 'overview' | 'leads' | 'sales' | 'tasks';

const tabs: { id: DashboardTab; label: string }[] = [
  { id: 'overview', label: 'Allgemein' },
  { id: 'leads', label: 'Leads' },
  { id: 'sales', label: 'Vertrieb' },
  { id: 'tasks', label: 'Aufgaben' },
];

const sourceLabels: Record<string, string> = {
  website: 'Website',
  empfehlung: 'Empfehlung',
  messe: 'Messe',
  kaltakquise: 'Kaltakquise',
  partner: 'Partner',
  bestandskunde: 'Bestandskunde',
  social_media: 'Social Media',
  sonstige: 'Sonstige',
  unbekannt: 'Unbekannt',
};

export function DashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');
  const [leadOffen, setLeadOffen] = useState(false);

  // Über react-query, damit Schnell erfassen und erledigte Aufgaben die
  // Zahlen sofort nachziehen (invalidateQueries(['dashboard'])).
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get(),
  });

  if (isLoading) {
    return (
      <div className="space-y-6 pt-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-48 w-full rounded-lg" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <>
        <CrmPageHeader title="Übersicht" />
        <EmptyState
          icon={AlertCircle}
          title="Die Übersicht ließ sich nicht laden"
          action={<Button onClick={() => refetch()}>Erneut laden</Button>}
        />
      </>
    );
  }

  const { sales_goal, pipeline, top_deals, tasks, leads } = data;
  const pipelineValue = pipeline.total_value;
  const dealsCount = pipeline.stages.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <div className="space-y-6">
      <CrmPageHeader
        title="Übersicht"
        subtitle={new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
        mehrAktionen={[
          { label: 'Lead anlegen', icon: UserPlus, onSelect: () => setLeadOffen(true) },
          { label: 'Pipeline öffnen', icon: TrendingUp, onSelect: () => navigate('/pipeline') },
        ]}
      >
        {/* Die Reiter gibt es nur am Rechner; am Handy steht darunter, was heute zählt. */}
        <div className="w-full max-md:hidden">
        <Tabs
          className="w-full"
          label="Bereiche der Übersicht"
          value={activeTab}
          onChange={(v) => setActiveTab(v as DashboardTab)}
          items={tabs.map((tab) => ({
            value: tab.id,
            label: tab.label,
            count:
              tab.id === 'leads' && leads && leads.total_leads > 0
                ? leads.total_leads
                : tab.id === 'tasks' && tasks.total_open > 0
                  ? tasks.total_open
                  : undefined,
          }))}
        />
        </div>
      </CrmPageHeader>

      {/* Am Handy (M2): zuerst die fälligen Aufgaben und die letzten Aktivitäten,
          dann die Kennzahlen kompakt und das Ziel. Diagramme, Umsatz-Einträge
          und die Reiter bleiben dem Rechner. */}
      <div className="space-y-6 md:hidden">
        <HeuteFaellig tasks={tasks} />
        <ActivityFeedWidget activities={data.recent_activities.slice(0, 5)} />
        <div className="grid grid-cols-2 gap-3">
          <StatTile label="Offene Aufgaben" value={String(tasks.total_open)} icon={ClipboardList} />
          <StatTile label="Offene Leads" value={String(leads?.total_leads ?? 0)} icon={UserPlus} />
          <StatTile label="Pipeline-Wert" value={formatEuro(pipelineValue)} icon={BarChart3} />
          <StatTile label="Gewonnen" value={formatEuro(sales_goal.closed_won_value)} icon={Trophy} />
        </div>
        <GoalTracker goal={sales_goal} />
      </div>

      <div className="space-y-6 max-md:hidden">
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Goal Tracker */}
          <GoalTracker goal={sales_goal} />

          {/* Manual Revenue Entries (feeds GoalTracker) */}
          <RevenueEntriesWidget onChange={() => refetch()} />

          {/* Quick KPIs */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Offene Leads"
              value={String(leads?.total_leads ?? 0)}
              icon={UserPlus}
              hint={`${leads?.new_this_week ?? 0} diese Woche`}
            />
            <StatTile
              label="Gewonnen"
              value={formatEuro(sales_goal.closed_won_value)}
              icon={Trophy}
              hint={`${sales_goal.closed_won_count} Anfragen`}
            />
            <StatTile
              label="Pipeline-Wert"
              value={formatEuro(pipelineValue)}
              icon={BarChart3}
              hint={`${dealsCount} aktive Anfragen`}
            />
            <StatTile
              label="Offene Aufgaben"
              value={String(tasks.total_open)}
              icon={ClipboardList}
              hint={tasks.overdue.length > 0 ? `${tasks.overdue.length} überfällig` : 'Keine überfällig'}
            />
          </div>

          {/* Activity Feed */}
          <ActivityFeedWidget activities={data.recent_activities} />
        </div>
      )}

      {activeTab === 'leads' && leads && (
        <div className="space-y-6">
          {/* Lead KPIs */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Offene Leads"
              value={String(leads.total_leads)}
              icon={UserPlus}
              hint={`${leads.new_this_week} neu diese Woche`}
            />
            <StatTile
              label="Konvertiert"
              value={String(leads.total_converted)}
              icon={Trophy}
              hint="zu Kunden geworden"
            />
            <StatTile
              label="Umwandlungsquote"
              value={`${leads.conversion_rate} %`}
              icon={Percent}
              hint="Lead → Kunde"
            />
            <StatTile
              label="Ältester Lead"
              value={leads.oldest_lead_days > 0 ? `${leads.oldest_lead_days} Tage` : '–'}
              icon={Target}
              hint={leads.oldest_lead_days > 14 ? 'Nachfassen' : 'Im Rahmen'}
            />
          </div>

          {/* Leads by Source */}
          <Card title="Leads nach Quelle">
            {leads.by_source.length === 0 ? (
              <p className="t-small text-muted-foreground">Leads erscheinen hier nach ihrer Quelle, sobald einer angelegt ist.</p>
            ) : (
              <div className="space-y-3">
                {leads.by_source
                  .sort((a, b) => b.count - a.count)
                  .map((item) => {
                    const maxCount = Math.max(...leads.by_source.map((s) => s.count));
                    const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    return (
                      <Meter
                        key={item.source}
                        label={<span className="block w-32 truncate">{sourceLabels[item.source] || item.label || item.source}</span>}
                        value={pct}
                        valueLabel={item.count}
                      />
                    );
                  })}
              </div>
            )}
          </Card>

          <div className="flex justify-center">
            <Button onClick={() => navigate('/leads')}>
              Alle Leads anzeigen
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'sales' && (
        <div className="space-y-6">
          {/* Sales KPIs */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Gewonnen"
              value={formatEuro(sales_goal.closed_won_value)}
              icon={Trophy}
              hint={`${sales_goal.closed_won_count} Anfragen gewonnen`}
            />
            <StatTile
              label="Pipeline-Wert"
              value={formatEuro(pipelineValue)}
              icon={BarChart3}
              hint={`${dealsCount} aktive Anfragen`}
            />
            <StatTile
              label="Gewichtete Pipeline"
              value={formatEuro(sales_goal.weighted_pipeline_value)}
              icon={Scale}
              hint="nach Wahrscheinlichkeit der Phase"
            />
            <StatTile
              label="Abschlussquote"
              value={`${sales_goal.conversion_rate} %`}
              icon={Percent}
              hint={`${sales_goal.closed_won_count} gewonnen, ${sales_goal.closed_lost_count} verloren`}
            />
          </div>

          {/* Pipeline + Top Deals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PipelineChart stages={pipeline.stages} totalValue={pipeline.total_value} />
            <AnfragenList deals={top_deals} />
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div className="space-y-6">
          {/* Tasks KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatTile
              label="Offen"
              value={String(tasks.total_open)}
              icon={ClipboardList}
              hint="Aufgaben insgesamt"
            />
            <StatTile
              label="Heute fällig"
              value={String(tasks.today.length)}
              icon={Target}
              hint={tasks.today.length === 0 ? 'Nichts fällig' : 'Aufgaben heute'}
            />
            <StatTile
              label="Überfällig"
              value={String(tasks.overdue.length)}
              icon={AlertCircle}
              hint={tasks.overdue.length === 0 ? 'Keine überfälligen' : 'Nachholen'}
            />
          </div>

          {/* Full Tasks Widget */}
          <TasksWidget tasks={tasks} />
        </div>
      )}
      </div>

      <LeadSheet open={leadOffen} onOpenChange={setLeadOffen} />
    </div>
  );
}
